<template>
  <BittsModal
    v-if="dataShareRequestModalVisible"
    :aria-label="modalTitle"
    class="c-request-data-modal"
    primary-button-text="Send request"
    :action-loading="requestLoading"
    :width="504"
    :hide-footer="!partnerSelectionMade"
    :disabled="noOptionsChecked"
    @closed="modalClosed"
    @action="requestSharedData"
  >
    <template #header>
      <div class="c-request-data-modal__header">
        <h3
          class="c-request-data-modal__title"
          data-testid="request-data-modal-title"
        >
          {{ modalTitle }}
        </h3>
        <p class="c-request-data-modal__subtitle">
          {{ modalSubtitle }}
        </p>
      </div>
    </template>
    <PartnerSelectionModal
      v-if="!partnerSelectionMade"
      :partner-orgs="filteredPartnerOrgs"
      @partner-selected="onPartnerSelected"
    />
    <div v-else>
      <div>
        <div class="font-bold text-base text-neutral-text-strong mb-12">
          Partner Information
        </div>
        <div class="c-request-data-modal__partner-info">
          <div class="c-request-data-modal__company-info">
            <BittsAvatar
              shape="square"
              :org="selectedPartner"
              size="small"
              class="mr-12"
            />
            <div class="c-request-data-modal__partner-org-name">
              {{ selectedPartner?.name }}
            </div>
          </div>
          <div
            v-if="hasSinglePopulation"
            class="c-request-data-modal__population-info"
          >
            <FontAwesomeIcon
              :icon="['fak', 'population']"
              :style="{ height: '16px', width: '16px' }"
              class="mr-8 self-center text-neutral-accent"
            />
            <div class="c-request-data-modal__population-name">
              {{ currentPopulation?.name }}
            </div>
          </div>
          <div v-else>
            <BittsTooltip placement="topRight">
              <BittsTag :text="`${numPops} populations`">
                <template #pre>
                  <FontAwesomeIcon
                    :icon="['fak', 'population']"
                    :style="{ height: '12px', width: '12px' }"
                    class="text-neutral-accent mr-4"
                  />
                </template>
              </BittsTag>
              <template #title>
                {{ popTooltipText }}
              </template>
            </BittsTooltip>
          </div>
        </div>
        <div v-if="!hasSinglePopulation" class="flex flex-col gap-8 mb-24">
          <div
            v-for="partnerPop in requestOptions"
            :key="partnerPop.id"
            :data-testid="`request-option--${partnerPop.id}`"
            @click="(e) => onPopulationSelected(partnerPop, e)"
          >
            <div class="flex justify-between">
              <BittsCheckbox
                :label="partnerPop.name"
                :checked="partnerPop.checked"
              />
              <BittsTag
                v-if="partnerPop.standard_type"
                text="Standard"
                color="info"
                size="x-small"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="c-request-data-modal__message-section">
        <BittsTextArea
          v-model="message"
          placeholder="Send your partner a detailed message describing the data or specific fields you’d like access to. "
          form-label="Add a message (optional)"
        />
      </div>
    </div>
  </BittsModal>
</template>
<script setup lang="ts">
import {
  BittsAvatar,
  BittsCheckbox,
  BittsModal,
  BittsTag,
  BittsTextArea,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { sortBy } from 'lodash';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import PartnerSelectionModal from '@/components/partners/PartnerSelectionModal.vue';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import { SHARING } from '@/constants/data_shares';
import {
  useDataRequestsStore,
  useFlashesStore,
  usePartnersStore,
} from '@/stores';
import {
  PartnerPopulationWithMetadata,
  RequestOption,
  ShareRequest,
} from '@/types/data_share_requests';
import { PartnerTrimmed } from '@/types/partners';

const { dataShareRequestModalVisible, orgIds, partnerPops } = defineProps<{
  dataShareRequestModalVisible?: boolean;
  orgIds?: number[] | null;
  partnerPops?: Record<number, PartnerPopulationWithMetadata[]> | null;
}>();

const emits = defineEmits(['modal-closed']);
const { iteratively } = useIteratively();
const requestLoading = ref(false);
const message = ref<string | undefined>(undefined);
const selectedPartner = ref<PartnerTrimmed | null>(null);
const selectedPopIds = ref<number[]>([]);

const partnersStore = usePartnersStore();

const { refreshDataRequestsStore } = useDataRequestsStore();
const { addErrorFlash, addSuccessFlash } = useFlashesStore();

const partnerOrgs = computed(() =>
  orgIds?.map((org) => partnersStore.getPartnerOrgById(org)),
);

const filteredPartnerOrgs = computed(() =>
  partnerOrgs.value?.filter(
    (org) =>
      org?.id &&
      partnerPops?.[org.id]?.some(
        (pop) =>
          !pop.dataShare.visibility.includes(SHARING) &&
          pop.requestDataIsVisible &&
          !pop.dataRequestInformation,
      ),
  ),
);
const currentPopulation = computed(() =>
  selectedPartner.value?.id && partnerPops?.[selectedPartner.value.id]
    ? partnerPops[selectedPartner.value.id]?.[0]
    : undefined,
);

const modalTitle = computed(() =>
  partnerSelectionMade.value ? 'Request Data' : 'Select a Partner',
);
const modalSubtitle = computed(() =>
  hasSinglePopulation.value
    ? 'Review partner information and add an optional message'
    : partnerSelectionMade.value
      ? 'Review population selections and add an optional message'
      : 'Select a partner to request data from',
);

const numPops = computed(
  () =>
    selectedPartner.value?.id &&
    partnerPops?.[selectedPartner.value.id]?.length,
);
const popTooltipText = computed(
  () =>
    selectedPartner.value?.id &&
    partnerPops?.[selectedPartner.value.id]?.map((pop) => pop.name).join(', '),
);
const partnerSelectionMade = computed(() => !!selectedPartner.value);
const hasSinglePopulation = computed(() => numPops.value === 1);

const requestOptions = ref<RequestOption[]>([]);
function setRequestOptions() {
  const partnerId = selectedPartner.value?.id;
  if (!partnerId) return;

  requestOptions.value = sortBy(
    partnerPops?.[partnerId]
      ?.filter((pop) => pop.requestDataIsVisible && !pop.dataRequestInformation)
      .reduce<RequestOption[]>((acc, pop, idx) => {
        selectedPopIds.value.push(pop.id);
        acc[idx] = { checked: true, ...pop };
        return acc;
      }, []),
    ['standard_type'],
  );
}

const noOptionsChecked = computed(
  () =>
    !!selectedPartner.value &&
    !hasSinglePopulation.value &&
    !requestOptions.value.filter((pop) => !!pop.checked).length,
);

watch(
  () => dataShareRequestModalVisible,
  (isCurrentlyVisible) => {
    if (isCurrentlyVisible) {
      selectedPartner.value =
        orgIds?.length === 1 ? partnerOrgs.value?.[0] || null : null;
      if (selectedPartner.value && !hasSinglePopulation.value)
        setRequestOptions();
    }
  },
);

const router = useRouter();
const route = useRoute();
async function modalClosed(response: ShareRequest | undefined | null = null) {
  selectedPopIds.value = [];
  message.value = undefined;
  if (route.query?.partnerPopId) {
    await router.replace({
      name: 'partnership-settings',
      params: {
        partner_org_id: route.params.partnerOrgId,
        tab: 'shared-with-you',
      },
    });
  }
  emits('modal-closed', response);
}

function onPartnerSelected(partner: PartnerTrimmed) {
  selectedPartner.value = partner;
  setRequestOptions();
}

function onPopulationSelected(partnerPop: RequestOption, e: Event) {
  const selectedPop = requestOptions.value.find(
    (opt) => opt.id === partnerPop.id,
  );
  if (!selectedPop) return;
  selectedPop.checked = (e.target as HTMLInputElement).checked;
  if (selectedPop.checked && !selectedPopIds.value.includes(selectedPop.id)) {
    selectedPopIds.value.push(selectedPop.id);
  } else {
    selectedPopIds.value = selectedPopIds.value.filter(
      (pop) => pop !== selectedPop.id,
    );
  }
}

async function requestSharedData() {
  if (!selectedPartner.value) return;
  requestLoading.value = true;

  const partnerPopsToRequest = selectedPopIds.value.length
    ? selectedPopIds.value
    : currentPopulation.value && currentPopulation.value.id
      ? [currentPopulation.value.id]
      : [];

  const payload = {
    partner_population_ids: partnerPopsToRequest,
    message: message.value || null,
    partner_org_id: selectedPartner.value.id,
  };

  const { data, error } = await crossbeamApi.POST('/v0.2/share-requests', {
    body: payload,
  });

  if (error) {
    addErrorFlash({
      message: 'Data Request Not Sent',
      description:
        'Please try again or contact support@crossbeam.com for help.',
    });
  } else {
    let requestId = data.items[0]?.id;
    if (data.items.length > 1) {
      requestId = data.items.map((request) => request.id).toString();
    }
    iteratively.userRequestedDataShare({
      share_request_id: String(requestId),
      partner_org_id: selectedPartner.value.id.toString(),
      partner_population_id: `${partnerPopsToRequest}`,
      event_site: EVENT_SITES.DATA_SHARE_REQUEST_MODAL,
    });
    modalClosed(data.items[0]);
    await refreshDataRequestsStore();
    addSuccessFlash({
      message: 'Data Request Sent',
      description:
        "You'll receive a notification if your partner shares fields from this population with you.",
    });
  }

  requestLoading.value = false;
}
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-request-data-modal__header {
  @apply w-full p-16 pb-8 text-left;
}

.c-request-data-modal__title {
  @apply text-neutral-text-strong font-bold text-xl text-left mb-8;
}

.c-request-data-modal__subtitle {
  @apply text-neutral-text text-m text-left;
}

.c-request-data-modal__message-section {
  @apply pb-4;
}

.c-request-data-modal__partner-info {
  @apply flex flex-row mb-24 justify-between;
}

.c-request-data-modal__company-info,
.c-request-data-modal__population-info {
  @apply flex flex-row text-neutral-text-strong content-center mr-auto;
}

.c-request-data-modal__partner-org-name {
  @apply text-neutral-text-strong text-m font-bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 188px;
}

.c-request-data-modal__population-name {
  @apply text-neutral-text-strong text-m;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 204px;
}
</style>
